import { Injectable } from '@angular/core';
import { AuthenticationResult } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';

import { securityLog } from '../helpers';
import { AuthAccountService, AuthRequestBuilderService, AuthSessionService, B2cAuthorityService, ProtectedResourceService } from '../services';

export interface SwitchTenantCommandResult {
    authenticationResult: AuthenticationResult;
    authority?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SwitchTenantCommand {
    constructor(
        private msalService: MsalService,
        private accountService: AuthAccountService,
        private authRequestBuilder: AuthRequestBuilderService,
        private authSession: AuthSessionService,
        private authorityService: B2cAuthorityService,
        private protectedResourceService: ProtectedResourceService,
    ) { }

    public async execute(tenantId: string, tenantSlug?: string): Promise<SwitchTenantCommandResult | undefined> {
        const normalizedTenantId = (tenantId || '').trim();
        const normalizedTenantSlug = (tenantSlug || '').trim();

        securityLog.debug('SwitchTenantCommand: execute', {
            tenantId: normalizedTenantId,
            tenantSlug: normalizedTenantSlug,
        });

        if (!normalizedTenantId) {
            securityLog.warn('SwitchTenantCommand: switchTenant called without tenant id');
            return undefined;
        }

        const activeAccount = this.accountService.getActiveAccount();
        if (!activeAccount) {
            securityLog.error('SwitchTenantCommand: cannot switch tenant without active account context');
            return undefined;
        }

        const idTokenHint = await this.authSession.resolveIdTokenHint(activeAccount);
        if (!idTokenHint) {
            securityLog.error('SwitchTenantCommand: cannot switch tenant without id token hint');
            return undefined;
        }

        const protectedResources = this.protectedResourceService.getProtectedResources();
        const request = this.authRequestBuilder.createSwitchTenantRedirectRequest(
            normalizedTenantId,
            idTokenHint.idToken,
            normalizedTenantSlug,
            this.authorityService.switchTenantAuthority,
            protectedResources.applicationApi.scopes.read,
            protectedResources.applicationApi.scopes.write,
        );

        this.msalService.acquireTokenRedirect(request);

        return {
            authenticationResult: idTokenHint.result,
            authority: idTokenHint.authority,
        };
    }
}